import { createSelector, Selector } from '@reduxjs/toolkit'
import moment from 'moment'
import { groupBy, orderBy, uniqBy } from 'lodash-es'
import { GlobalState } from 'store'
import {
  AuditDetail,
  AuditStatus,
  Collection,
  EntityType,
  NegotiationStatus,
  Organisation,
  TradeConditionFlag,
  TradingEntity,
  NegotiationHistorySubscriptionIdentifier,
  EntityTypeEnum,
  NegotiationSide
} from 'api'
import { isActive, UnassignedCollectionName, compareCreatedAtDesc, createSponsorFromCounterparty } from 'model'
import { Locale, Translation } from 'locale'
import { createEntityGetterSelector, createEntityStoreSelector } from './entitySelectors'
import { isOwnTradingEntityGetterSelector } from './adminSelectors'
import { createTradingEntityWithAliasGetterSelector } from './tradingEntitySelectors/tradingEntitySelectors'
import { createAuthorisedUserSelector } from './connectionSelectors'
import { showOrgActivitySelector } from './gridSelectors'
import { isSummaryNegotiationType } from 'view/routes/requests/shared/utils'
import { TREE_DATA_GROUP_BY_COLLECTION_KEY } from 'view/routes/ColumnNames'

export function createNegotiationHistoryEntrySelector(subscriptionIdentifiers: NegotiationHistorySubscriptionIdentifier[]) {
  return createSelector(
    [
      createEntityStoreSelector(EntityTypeEnum.NEGOTIATION_HISTORY),
      isOwnTradingEntityGetterSelector,
      createAuthorisedUserSelector(),
      showOrgActivitySelector
    ],
    (negotiationHistoryStore, getIsOwnTradingEntity, authorisedUser, showOrgActivity) =>
      Object.values(negotiationHistoryStore)
        .filter(entry => {
          if (!getIsOwnTradingEntity(entry.lastUpdate.entity.fundId) && !getIsOwnTradingEntity(entry.lastUpdate.entity.counterpartyId)) {
            return false
          }
          if (isSummaryNegotiationType(entry.lastUpdate.entity.negotiationType)) {
            return false
          }
          if (
            subscriptionIdentifiers.every(
              subscriptionIdentifier => !entry.lastUpdate.subscriptionIdentifiers.includes(subscriptionIdentifier)
            )
          ) {
            return false
          }
          if (!showOrgActivity) {
            return entry.lastUpdate.entity.createdBy === authorisedUser.username
          }
          return true
        })
        .sort(compareCreatedAtDesc)
  )
}

export function createNegotiationHistoryDetailsSelector(
  subscriptionIdentifiers: NegotiationHistorySubscriptionIdentifier[] = []
): Selector<GlobalState, AuditDetail[]> {
  return createSelector(
    createNegotiationHistoryEntrySelector(subscriptionIdentifiers),
    isOwnTradingEntityGetterSelector,
    createEntityGetterSelector(EntityType.COLLECTION),
    createEntityGetterSelector(EntityType.COLLATERAL),
    createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
    createEntityGetterSelector(EntityType.CLEARING_HOUSE),
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    createTradingEntityWithAliasGetterSelector(),
    createEntityGetterSelector(EntityType.ORGANISATION),
    createEntityGetterSelector(EntityType.PRODUCT),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    createEntityGetterSelector(EntityType.REFERENCE_RATE),
    createEntityGetterSelector(EntityType.SPONSOR),
    createEntityGetterSelector(EntityType.USER),
    createEntityGetterSelector(EntityType.NOTES),
    (
      negotiationHistoryEntries,
      getIsOwnTradingEntity,
      getCollection,
      getCollateral,
      getCollateralBasket,
      getClearingHouse,
      getTradingEntity,
      getTradingEntityWithAlias,
      getOrganisation,
      getProduct,
      getProductSector,
      getReferenceRate,
      getSponsor,
      getUser,
      getNotes
    ) => {
      const negotiationHistoryUpdates = negotiationHistoryEntries.map(entry => entry.lastUpdate)
      const negotiationSideLookup = negotiationHistoryUpdates.reduce((acc, negotiationHistoryUpdate) => {
        acc[negotiationHistoryUpdate.entityId] = negotiationHistoryUpdate.negotiationSide
        return acc
      }, {} as Record<string, NegotiationSide>)

      return negotiationHistoryUpdates.map(negotiationHistoryUpdate => {
        const auditId = `${negotiationHistoryUpdate.entity.parentOrderId}/${negotiationHistoryUpdate.entityId}`
        const negotiationHistory = negotiationHistoryUpdate.entity
        // We need to check the negotiation side on the parent negotiation because of this scenario:
        // - the same organisation is on both sides of the negotiation
        // - the user does not have permission to view initiated negotiations
        // In this scenario getIsOwnTradingEntity() will return true,
        // so without the check we would see the nested history items from the initiator's rather than the responder's viewpoint
        const negotiationSide = negotiationSideLookup[negotiationHistoryUpdate.entityId] ?? NegotiationSide.INITIATOR
        const isOwnNegotiation = negotiationSide === NegotiationSide.INITIATOR && getIsOwnTradingEntity(negotiationHistory.fundId)
        const collection = negotiationHistory.collectionId ? getCollection(negotiationHistory.collectionId) : null

        const collateral = negotiationHistory.collateralId ? getCollateral(negotiationHistory.collateralId) : null
        const collateralBasket = negotiationHistory.basketAllocation
          ? getCollateralBasket(negotiationHistory.basketAllocation.basketId)
          : null
        const productSector = collateral
          ? getProductSector(collateral.productSectorId)
          : collateralBasket
          ? getProductSector(collateralBasket.productSectorId)
          : null
        const product = productSector ? getProduct(productSector.productId) : null

        const counterparty = negotiationHistory.counterpartyId
          ? isOwnNegotiation
            ? getTradingEntityWithAlias(negotiationHistory.fundId, negotiationHistory.counterpartyId)
            : getTradingEntity(negotiationHistory.counterpartyId)
          : null
        const counterpartyFirm = counterparty ? getOrganisation(counterparty.organisationId) : null
        const orderFund = negotiationHistory.fundId
          ? isOwnNegotiation
            ? getTradingEntity(negotiationHistory.fundId)
            : getTradingEntityWithAlias(negotiationHistory.counterpartyId, negotiationHistory.fundId)
          : null
        const orderFundFirm = orderFund ? getOrganisation(orderFund.organisationId) : null

        const clearingHouse = negotiationHistory.clearingHouseId ? getClearingHouse(negotiationHistory.clearingHouseId) : null
        const sponsor = negotiationHistory.sponsorId
          ? negotiationHistory.isSponsorCounterparty
            ? createSponsorFromCounterparty(counterparty)
            : getSponsor(negotiationHistory.sponsorId)
          : null
        const counterpartySponsorId = negotiationHistory.counterpartySponsorId
        const counterpartySponsor = counterpartySponsorId ? getSponsor(counterpartySponsorId) : null

        const referenceRate = negotiationHistory.referenceRate ? getReferenceRate(negotiationHistory.referenceRate) : null
        const axeTradeCondition = {
          isAxed: !!negotiationHistory.axed,
          tradeCondition: negotiationHistory.tradeConditionFlag || TradeConditionFlag.NONE
        }
        const initiatorExecutionDecisionMaker = negotiationHistory.initiatorExecutionDecisionMaker
          ? getUser(negotiationHistory.initiatorExecutionDecisionMaker)
          : null
        const initiatorInvestmentDecisionMaker = negotiationHistory.initiatorInvestmentDecisionMaker
          ? getUser(negotiationHistory.initiatorInvestmentDecisionMaker)
          : null
        const responderExecutionDecisionMaker = negotiationHistory.responderExecutionDecisionMaker
          ? getUser(negotiationHistory.responderExecutionDecisionMaker)
          : null
        const responderInvestmentDecisionMaker = negotiationHistory.responderInvestmentDecisionMaker
          ? getUser(negotiationHistory.responderInvestmentDecisionMaker)
          : null
        const fundPrimarySalesCoverUser = negotiationHistory.fundPrimaryCoverUserId
          ? getUser(negotiationHistory.fundPrimaryCoverUserId)
          : null
        const fundSecondarySalesCoverUser = negotiationHistory.fundSecondaryCoverUserId
          ? getUser(negotiationHistory.fundSecondaryCoverUserId)
          : null
        const counterpartyPrimarySalesCoverUser = negotiationHistory.counterpartyPrimaryCoverUserId
          ? getUser(negotiationHistory.counterpartyPrimaryCoverUserId)
          : null
        const counterpartySecondarySalesCoverUser = negotiationHistory.counterpartySecondaryCoverUserId
          ? getUser(negotiationHistory.counterpartySecondaryCoverUserId)
          : null
        const notes = negotiationHistory.notesId ? getNotes(negotiationHistory.notesId) : null

        const myEntityShortName = isOwnNegotiation
          ? orderFund?.shortName
            ? orderFund.shortName
            : null
          : counterparty?.shortName
          ? counterparty.shortName
          : null

        return {
          auditId,
          negotiation: negotiationHistory,
          direction: negotiationHistory.direction,
          collection,
          collateral,
          collateralBasket,
          clearingHouse,
          sponsor,
          counterpartySponsor,
          productSector,
          product,
          counterparty,
          counterpartyFirm,
          orderFund,
          orderFundFirm,
          referenceRate,
          axeTradeCondition,
          auditStatus: null,
          ownOrder: isOwnNegotiation,
          excludeFromBestPriceCalculations: false,
          initiatorExecutionDecisionMaker,
          initiatorInvestmentDecisionMaker,
          responderExecutionDecisionMaker,
          responderInvestmentDecisionMaker,
          fundPrimarySalesCoverUser,
          fundSecondarySalesCoverUser,
          counterpartyPrimarySalesCoverUser,
          counterpartySecondarySalesCoverUser,
          notes,
          validationIssues: [],
          availableEvents: [],
          myEntityShortName
        }
      })
    }
  )
}

function createTargetAuditDetailsSelector(): Selector<GlobalState, AuditDetail[]> {
  return createSelector(
    createNegotiationHistoryDetailsSelector([NegotiationHistorySubscriptionIdentifier.LATEST]),
    negotiationHistoryDetails =>
      negotiationHistoryDetails.map(negotiationHistoryDetail => {
        return {
          ...negotiationHistoryDetail,
          auditId: negotiationHistoryDetail.negotiation.parentOrderId,
          negotiationHistory: {
            ...negotiationHistoryDetail.negotiation,
            notional: negotiationHistoryDetail.negotiation.notional,
            rate: negotiationHistoryDetail.negotiation.rate,
            haircut: negotiationHistoryDetail.negotiation.haircut,
            startDate: negotiationHistoryDetail.negotiation.startDate,
            endDate: negotiationHistoryDetail.negotiation.endDate,
            term: negotiationHistoryDetail.negotiation.term,
            bondPrice: negotiationHistoryDetail.negotiation.bondPrice,
            startConsideration: negotiationHistoryDetail.negotiation.startConsideration,
            counterpartySponsorId: negotiationHistoryDetail.negotiation.counterpartySponsorId,
            tradeConditionFlag: negotiationHistoryDetail.negotiation.tradeConditionFlag,
            isAxed: negotiationHistoryDetail.negotiation.axed,
            initiatorExecutionDecisionMaker: negotiationHistoryDetail.negotiation.initiatorExecutionDecisionMaker,
            initiatorInvestmentDecisionMaker: negotiationHistoryDetail.negotiation.initiatorInvestmentDecisionMaker,
            responderExecutionDecisionMaker: negotiationHistoryDetail.negotiation.responderExecutionDecisionMaker,
            responderInvestmentDecisionMaker: negotiationHistoryDetail.negotiation.responderInvestmentDecisionMaker
          },
          auditStatus: null
        }
      })
  )
}

const MULTIPLE_VALUES = Locale.translate(Translation.SC_MULTIPLE)

function createSummaryAuditDetailsSelector(): Selector<GlobalState, AuditDetail[]> {
  return createSelector(createTargetAuditDetailsSelector(), targetAuditDetails => {
    const targetAuditDetailsByParent = Object.values(groupBy(targetAuditDetails, 'negotiation.parentOrderId'))

    return targetAuditDetailsByParent.map(auditDetails => {
      const numberOfCounterparties = uniqBy(auditDetails, auditDetail => auditDetail.negotiation.counterpartyId).length
      const multipleCounterparties = numberOfCounterparties > 1

      const ongoing = auditDetails.filter(auditDetail => !isActive(auditDetail.negotiation.status)).length < numberOfCounterparties
      const auditStatus = ongoing ? AuditStatus.ONGOING : AuditStatus.COMPLETED

      if (auditDetails.length === 1) {
        return {
          ...auditDetails[0],
          auditStatus
        }
      } else {
        return auditDetails.reduce((summaryAuditDetail, auditDetail) => {
          const isSummaryNewer = moment(summaryAuditDetail.negotiation.lastUpdatedAt).isSameOrAfter(auditDetail.negotiation.lastUpdatedAt)

          const newerAuditDetail =
            summaryAuditDetail.negotiation.status === NegotiationStatus.ACCEPTED
              ? summaryAuditDetail
              : auditDetail.negotiation.status === NegotiationStatus.ACCEPTED
              ? auditDetail
              : isSummaryNewer
              ? summaryAuditDetail
              : auditDetail

          return {
            ...newerAuditDetail,
            negotiation: {
              ...newerAuditDetail.negotiation,
              negotiationId: multipleCounterparties ? MULTIPLE_VALUES : newerAuditDetail.negotiation.negotiationId,
              counterpartyId: multipleCounterparties ? MULTIPLE_VALUES : newerAuditDetail.negotiation.counterpartyId,
              tradeConditionFlag: multipleCounterparties ? null! : newerAuditDetail.negotiation.tradeConditionFlag,
              axed: multipleCounterparties ? null! : newerAuditDetail.negotiation.axed,
              lastResponderId: multipleCounterparties ? MULTIPLE_VALUES : newerAuditDetail.negotiation.lastResponderId
            },
            counterparty: {
              publishedName: multipleCounterparties ? MULTIPLE_VALUES : newerAuditDetail.counterparty?.publishedName,
              lei: multipleCounterparties ? MULTIPLE_VALUES : newerAuditDetail.counterparty?.lei
            } as TradingEntity,
            counterpartyFirm: {
              legalName: multipleCounterparties ? MULTIPLE_VALUES : newerAuditDetail.counterpartyFirm?.legalName,
              lei: multipleCounterparties ? MULTIPLE_VALUES : newerAuditDetail.counterpartyFirm?.lei
            } as Organisation,
            auditStatus
          }
        })
      }
    })
  })
}

function createChildNegotiationHistoryDetailsSelector(): Selector<GlobalState, AuditDetail[]> {
  return createSelector(
    createNegotiationHistoryDetailsSelector([
      NegotiationHistorySubscriptionIdentifier.LATEST,
      NegotiationHistorySubscriptionIdentifier.HISTORY
    ]),
    auditDetails => {
      const auditDetailsByParent = groupBy(auditDetails, auditDetail => auditDetail.negotiation.parentOrderId)
      return Object.values(auditDetailsByParent).reduce<AuditDetail[]>((acc, auditDetailsOfParent) => {
        const [expiredRejectedAuditDetails, otherAuditDetails] = auditDetailsOfParent.reduce<[AuditDetail[], AuditDetail[]]>(
          (previousValue, auditDetail) => {
            if (
              auditDetail.negotiation.status === NegotiationStatus.REJECTED ||
              auditDetail.negotiation.status === NegotiationStatus.EXPIRED
            ) {
              return [[...previousValue[0], auditDetail], previousValue[1]]
            } else {
              return [previousValue[0], [...previousValue[1], auditDetail]]
            }
          },
          [[], []]
        )

        if (expiredRejectedAuditDetails.length > 1) {
          return [...acc, ...otherAuditDetails, expiredRejectedAuditDetails[expiredRejectedAuditDetails.length - 1]]
        } else {
          return [...acc, ...auditDetailsOfParent]
        }
      }, [])
    }
  )
}

const createCollectionKey = (collection: Collection | null) => {
  if (!collection) {
    return UnassignedCollectionName
  }
  const sanitizedCollectionName = collection.name.replace('/', '&#47;')
  return `${TREE_DATA_GROUP_BY_COLLECTION_KEY}_${sanitizedCollectionName}-${collection.id}`
}

export function createAuditDetailsSelector(): Selector<GlobalState, AuditDetail[]> {
  return createSelector(
    createSummaryAuditDetailsSelector(),
    createChildNegotiationHistoryDetailsSelector(),
    (summaryAuditDetails, auditDetails) => {
      const allAuditDetails = orderBy(
        [...summaryAuditDetails, ...auditDetails],
        ['negotiation.lastUpdatedAt', 'negotiation.parentOrderId'],
        ['desc', 'asc']
      )
      const groupByCollection = allAuditDetails.some(auditDetail => !!auditDetail.collection)

      return allAuditDetails.map(auditDetail => {
        // Amend the auditId on all entries, adding the collection name to the beginning if its in a collection
        if (groupByCollection) {
          const collectionName = createCollectionKey(auditDetail.collection)
          const auditId = `${collectionName}/${auditDetail.auditId}`
          return {
            ...auditDetail,
            auditId
          }
        }

        return auditDetail
      })
    }
  )
}
